import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { KeyAuditOperation, WebhookKeyAuditOp } from "../../types";

const OP_LABELS: Record<WebhookKeyAuditOp, { label: string; color: "default" | "primary" | "warning" | "success" | "error" | "info" }> = {
  created: { label: "Creazione", color: "success" },
  disabled: { label: "Disabilitazione", color: "warning" },
  enabled: { label: "Riattivazione", color: "info" },
  revoked: { label: "Revoca", color: "error" },
  rotated: { label: "Rotazione", color: "primary" },
  updated: { label: "Modifica metadati", color: "default" },
};

interface Props {
  logs: KeyAuditOperation[];
}

export default function WebhookKeyAuditLog({ logs }: Props) {
  const [metaPreview, setMetaPreview] = useState<KeyAuditOperation | null>(null);

  if (logs.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        Nessun evento registrato per questa chiave.
      </Typography>
    );
  }

  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Data/ora</TableCell>
              <TableCell width={180}>Operazione</TableCell>
              <TableCell>Attore</TableCell>
              <TableCell width={120}>Dettagli</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((log) => {
              const op = OP_LABELS[log.operation] ?? { label: log.operation, color: "default" as const };
              const hasMeta = !!log.meta && Object.keys(log.meta).length > 0;
              return (
                <TableRow key={log.id} hover>
                  <TableCell>
                    <Typography variant="caption" fontFamily="monospace">
                      {new Date(log.created_at).toLocaleString("it-IT")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={op.label} size="small" color={op.color} variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {log.actor_email ?? log.actor_uid}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {hasMeta ? (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => setMetaPreview(log)}
                      >
                        Mostra
                      </Button>
                    ) : (
                      <Typography variant="caption" color="text.disabled">
                        —
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!metaPreview} onClose={() => setMetaPreview(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Dettagli operazione</DialogTitle>
        <DialogContent>
          {metaPreview && (
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1.5,
                bgcolor: "grey.100",
                fontSize: "0.75rem",
                fontFamily: "monospace",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                maxHeight: 400,
                overflow: "auto",
                border: "1px solid",
                borderColor: "grey.300",
                borderRadius: 1,
              }}
            >
              {JSON.stringify(metaPreview.meta, null, 2)}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMetaPreview(null)}>Chiudi</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
